/**
 * A live call: take a seat, publish, pull whoever else is there, hang up.
 *
 * This is the browser's `CallController` (`Programs/relay/src/media/callcontroller.h`),
 * and it is a state machine rather than a sequence for the same reason: every
 * step can arrive late or not at all, and a call whose steps only work in the
 * order they were written is a call that breaks on the first slow network.
 *
 * ── The order, and why it is that order ─────────────────────────────────────
 * The seat comes first, then the microphone. Building a pipeline before knowing
 * there is a call to join opens the microphone for a request that may be
 * refused, and a microphone light that flickers on a rejected call is the kind
 * of thing people notice and do not forget.
 *
 * ── Publishing and subscribing are not symmetrical ──────────────────────────
 * Publishing is us describing what we will send: our offer, the SFU's answer,
 * done. Subscribing reverses it — the SFU describes what it is about to send,
 * we answer, and that answer goes back as a *renegotiation*. So a pull is two
 * round trips and is not finished when `POST …/tracks` returns; releasing the
 * connection there lets the next pull start mid-exchange, which is the same
 * fault one step later. Every exchange here is one `await`ed function inside
 * `NegotiationQueue`, which is what makes that impossible to get wrong twice.
 *
 * ── The roster arrives twice, and only one of them can be pulled from ───────
 * The `callstate` frame is the cheap one — it is relayed on every join, leave
 * and sweep — but it deliberately carries no SFU session id, so nothing can be
 * pulled from it (`relay-server/worker/do/conversation.ts`: "Never the SFU
 * session id"). It is a nudge: `applyCallState` reads a seat for this device
 * and asks for the real roster. The real roster is the 3-second read, which is
 * also the heartbeat that keeps this seat alive — one request doing both, not
 * a poll beside a ping.
 *
 * ── Nothing here knocks on a lobby ──────────────────────────────────────────
 * `join()` assumes the caller is admitted. The lobby is the meeting page's, and
 * `admissionChanged` is emitted by whoever owns that decision, not by the media.
 */

import { api } from '../api';
import type { Admission } from '../relay/types';
import { BackgroundProcessor } from './background';
import {
  browserMedia,
  camConstraints,
  createRemoteAudio,
  DEFAULT_AUDIO_SETTINGS,
  listDevices,
  micConstraints,
  type AudioSettings,
  type MediaAccess,
  type RemoteAudio
} from './devices';
import { iceServers } from './ice';
import { NegotiationCancelled, NegotiationQueue } from './negotiation';
import {
  browserRecorderFactory,
  ChunkRecorder,
  uploadToMeeting,
  type RecorderFactory,
  type UploadChunk
} from './recorder';
import {
  browserDeviceId,
  relaySignalling,
  roomPath,
  trackKind,
  trackName,
  type LocalTrackSpec,
  type Request,
  type RosterSeat,
  type SessionDescriptionInit,
  type Signalling,
  type TrackKind
} from './sfu';
import { SpeakingDetector } from './speaking';
import type {
  Background,
  CallEvent,
  CallEvents,
  CallSession,
  CallStats,
  CallStatus,
  DeviceList,
  JoinOptions,
  LocalState,
  Recording,
  RemotePeer,
  RemoteTracks,
  Room
} from './types';

/** How often the roster is read. Also the heartbeat: the read touches this
 *  seat, and `SEAT_TTL_MS` on the server is 45 seconds. */
export const ROSTER_POLL_MS = 3_000;

// ── the slice of WebRTC this file uses ───────────────────────────────────────
//
// Structural, so a test can hand over a fake peer connection without a DOM and
// without pretending to be a whole `RTCPeerConnection`.

export interface TransceiverLike {
  mid: string | null;
  direction: string;
  sender: { replaceTrack(track: MediaStreamTrack | null): Promise<void> };
}

export interface TrackEventLike {
  track: MediaStreamTrack;
  transceiver: { mid: string | null };
}

export interface PeerConnectionLike {
  addTransceiver(track: MediaStreamTrack, init: { direction: string }): TransceiverLike;
  createOffer(): Promise<SessionDescriptionInit>;
  createAnswer(): Promise<SessionDescriptionInit>;
  setLocalDescription(description: SessionDescriptionInit): Promise<void>;
  setRemoteDescription(description: SessionDescriptionInit): Promise<void>;
  getStats(): Promise<{ forEach(callback: (stat: Record<string, unknown>) => void): void }>;
  close(): void;
  ontrack: ((event: TrackEventLike) => void) | null;
}

export interface SpeakingLike {
  watch(id: string, track: MediaStreamTrack): void;
  unwatch(id: string): void;
  stop(): void;
}

export interface BackgroundLike {
  apply(track: MediaStreamTrack, background: Background): Promise<MediaStreamTrack>;
  stop(): void;
}

/** Everything this session reaches outside itself. Assembled by
 *  `browserDeps`; a test replaces the two or three it cares about. */
export interface CallDeps {
  signalling: Signalling;
  /** STUN, and TURN when the server has it. Never rejects. */
  iceServers: () => Promise<RTCIceServer[]>;
  createConnection: (iceServers: RTCIceServer[]) => PeerConnectionLike;
  media: MediaAccess;
  remoteAudio: RemoteAudio;
  background: BackgroundLike;
  createDetector: (onChange: (id: string, speaking: boolean) => void) => SpeakingLike;
  createRecorder: RecorderFactory;
  uploadChunk: (meetingId: string) => UploadChunk;
  request: Request;
  deviceId: string;
  audio: AudioSettings;
  now: () => number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

export function browserDeps(room: Room): CallDeps {
  return {
    signalling: relaySignalling(room),
    iceServers,
    createConnection: (servers) =>
      new RTCPeerConnection({
        iceServers: servers,
        bundlePolicy: 'max-bundle'
      }) as unknown as PeerConnectionLike,
    media: browserMedia(),
    remoteAudio: createRemoteAudio(),
    background: new BackgroundProcessor(),
    createDetector: (onChange) => new SpeakingDetector({ onChange }),
    // Built per slice rather than once: `isTypeSupported` is a browser call,
    // and nothing here should make one before somebody records.
    createRecorder: (track) => browserRecorderFactory()(track),
    uploadChunk: (meetingId) => uploadToMeeting(meetingId),
    request: api,
    deviceId: browserDeviceId(),
    audio: DEFAULT_AUDIO_SETTINGS,
    now: () => Date.now(),
    setTimer: (fn, ms) => setInterval(fn, ms),
    clearTimer: (handle) => clearInterval(handle as ReturnType<typeof setInterval>)
  };
}

/** One track this session is sending. */
interface Publication {
  kind: TrackKind;
  name: string;
  mid: string;
  track: MediaStreamTrack;
  transceiver: TransceiverLike;
}

/** What a pull asked for, keyed by the mid the SFU answered with, so `ontrack`
 *  knows whose track just arrived. A track carries no name of its own. */
interface Expectation {
  participantId: string;
  kind: TrackKind;
}

const EMPTY_TRACKS: RemoteTracks = {};

export class LiveCallSession implements CallSession {
  readonly room: Room;
  status: CallStatus = 'idle';
  local: LocalState = {
    mic: true,
    cam: false,
    screen: false,
    muted: false,
    deafened: false,
    pushToTalk: false
  };

  readonly recording: Recording;

  private readonly deps: CallDeps;
  private readonly queue: NegotiationQueue;
  private readonly detector: SpeakingLike;
  private readonly handlers = new Map<CallEvent, Set<unknown>>();

  private pc: PeerConnectionLike | null = null;
  private sessionId = '';
  private callId = '';
  private since = 0;
  private suffix = '';

  private audio: AudioSettings;
  private micTrack: MediaStreamTrack | null = null;
  private camTrack: MediaStreamTrack | null = null;
  private camSource: MediaStreamTrack | null = null;
  private screenTracks: MediaStreamTrack[] = [];
  private background: Background = { kind: 'none' };

  private readonly published: Publication[] = [];
  /** `sessionId/trackName` for everything queued, in flight or done: the set of
   *  things not to ask for again, and the roster hands the same peers back
   *  every three seconds. */
  private readonly pulled = new Set<string>();
  private readonly expecting = new Map<string, Expectation>();
  private readonly peerMap = new Map<string, RemotePeer>();

  private pollHandle: unknown = null;
  private rejoining = false;
  private leaving = false;
  private chunks: ChunkRecorder | null = null;
  private lastStats: { at: number; inBytes: number; outBytes: number } | null = null;

  constructor(room: Room, deps: CallDeps) {
    this.room = room;
    this.deps = deps;
    this.audio = deps.audio;
    this.queue = new NegotiationQueue({
      onStalled: (label) => {
        // Not a failure: one stalled pull is one peer, and the roster will
        // offer them again in three seconds.
        console.warn(`[call] ${label} never completed; releasing the queue`);
      },
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>)
    });
    this.detector = deps.createDetector((id, speaking) => this.onSpeaking(id, speaking));
    this.recording = {
      active: false,
      start: () => this.setRecordingFlag(true),
      stop: () => this.setRecordingFlag(false)
    };
  }

  peers(): RemotePeer[] {
    return [...this.peerMap.values()];
  }

  // ── joining ───────────────────────────────────────────────────────────────

  async join(opts: JoinOptions = {}): Promise<void> {
    if (this.status !== 'idle' && this.status !== 'ended') return;
    this.setStatus('joining');

    try {
      // Asked alongside the seat rather than after it: the answer is usually
      // cached, and when it is not it should not add a round trip to joining.
      const ice = this.deps.iceServers();
      const seat = await this.deps.signalling.join(this.deps.deviceId, opts.callId);
      this.adoptSession(seat.sessionId, seat.callId, seat.since);
      this.setStatus('connecting');

      await this.openMicrophone(opts.devices?.micId);
      if (opts.camera) await this.openCamera(opts.devices?.camId);
      if (opts.devices?.speakerId) await this.deps.remoteAudio.setSpeaker(opts.devices.speakerId);

      this.pc = this.openConnection(await ice);
      await this.publishLocal();
      this.setStatus('inCall');

      this.startPolling();
      this.applyRoster(seat.roster);
    } catch (error) {
      this.fail(error);
    }
  }

  private adoptSession(sessionId: string, callId?: string, since?: number): void {
    this.sessionId = sessionId;
    this.callId = callId ?? this.callId;
    if (since) this.since = since;
    if (!this.since) this.since = this.deps.now();
    // The track-name suffix is derived from the session so it is unique without
    // a second source of ids.
    this.suffix = sessionId.replace(/[^A-Za-z0-9_-]/g, '').slice(-8) || 'web';
  }

  private openConnection(servers: RTCIceServer[]): PeerConnectionLike {
    const pc = this.deps.createConnection(servers);
    pc.ontrack = (event) => this.onRemoteTrack(event);
    return pc;
  }

  private async openMicrophone(deviceId?: string): Promise<void> {
    const stream = await this.deps.media.getUserMedia(micConstraints(this.audio, deviceId));
    const track = stream.getAudioTracks()[0];
    if (!track) throw new Error('No microphone was available.');
    track.enabled = !this.local.muted;
    this.detector.unwatch('local');
    this.micTrack = track;
    this.local = { ...this.local, mic: true };
    this.detector.watch('local', track);
  }

  private async openCamera(deviceId?: string): Promise<void> {
    const stream = await this.deps.media.getUserMedia(camConstraints(deviceId));
    const source = stream.getVideoTracks()[0];
    if (!source) throw new Error('No camera was available.');
    this.camSource = source;
    this.camTrack = await this.deps.background.apply(source, this.background);
    this.local = { ...this.local, cam: true };
  }

  // ── publishing ────────────────────────────────────────────────────────────

  /** Everything this session is sending that is not published yet, in one
   *  exchange. One offer for the microphone and the camera together is one
   *  negotiation rather than two. */
  private async publishLocal(): Promise<void> {
    const waiting: Array<{ kind: TrackKind; track: MediaStreamTrack; name: string }> = [];
    if (this.micTrack) waiting.push({ kind: 'mic', track: this.micTrack, name: trackName('mic', this.suffix) });
    if (this.camTrack) waiting.push({ kind: 'cam', track: this.camTrack, name: trackName('cam', this.suffix) });
    for (const [index, track] of this.screenTracks.entries()) {
      waiting.push({
        kind: 'screen',
        track,
        name: index === 0 ? trackName('screen', this.suffix) : `${trackName('screen', this.suffix)}-audio`
      });
    }

    const fresh = waiting.filter((w) => !this.published.some((p) => p.name === w.name));
    if (fresh.length === 0) return;

    await this.queue.run('publish', async () => {
      const pc = this.pc;
      if (!pc || !this.sessionId) return;

      const added = fresh.map((w) => ({
        ...w,
        transceiver: pc.addTransceiver(w.track, { direction: 'sendonly' })
      }));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // A mid exists only once the local description is set — before that every
      // transceiver answers null, and the SFU refuses a track with no media
      // line to attach it to.
      const tracks: LocalTrackSpec[] = added.map((a) => ({
        location: 'local',
        mid: a.transceiver.mid ?? '',
        trackName: a.name
      }));

      const answer = await this.deps.signalling.publish(
        this.sessionId,
        { sdp: offer.sdp, type: 'offer' },
        tracks
      );
      if (!answer.sessionDescription) throw new Error('The server did not answer the call.');
      await pc.setRemoteDescription(answer.sessionDescription);

      for (const a of added) {
        this.published.push({
          kind: a.kind,
          name: a.name,
          mid: a.transceiver.mid ?? '',
          track: a.track,
          transceiver: a.transceiver
        });
      }
    });
  }

  /** Stop sending one kind. Closing a local track is a renegotiation of its
   *  own: the transceiver goes inactive, we offer that, and the SFU answers. */
  private async unpublish(kind: TrackKind): Promise<void> {
    const going = this.published.filter((p) => p.kind === kind);
    if (going.length === 0) return;
    for (const p of going) this.published.splice(this.published.indexOf(p), 1);

    await this.queue
      .run(`close ${kind}`, async () => {
        const pc = this.pc;
        if (!pc || !this.sessionId) return;
        for (const p of going) p.transceiver.direction = 'inactive';
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const answer = await this.deps.signalling.closeTracks(
          this.sessionId,
          going.map((p) => p.mid),
          { sdp: offer.sdp, type: 'offer' }
        );
        if (answer.sessionDescription) await pc.setRemoteDescription(answer.sessionDescription);
      })
      .catch((error: unknown) => this.report(error));

    for (const p of going) p.track.stop();
  }

  // ── pulling ───────────────────────────────────────────────────────────────

  /**
   * Queue everybody in the roster we are not already pulling.
   *
   * Queue, not pull: a connection carries one negotiation at a time, and
   * pulling a roster the moment it arrives — before our own publish has been
   * answered — is the fault `NegotiationQueue` exists to prevent.
   */
  private applyRoster(roster: RosterSeat[]): void {
    const seen = new Set<string>();

    for (const seat of roster) {
      if (seat.sessionId === this.sessionId) continue;
      seen.add(seat.userId);

      const existing = this.peerMap.get(seat.userId);
      this.peerMap.set(seat.userId, {
        participantId: seat.userId,
        displayName: seat.displayName ?? existing?.displayName ?? '',
        guest: seat.guest ?? existing?.guest ?? false,
        role: seat.role ?? existing?.role ?? 'attendee',
        muted: seat.muted ?? false,
        speaking: existing?.speaking ?? false,
        tracks: existing?.tracks ?? EMPTY_TRACKS
      });

      for (const name of seat.trackNames) {
        const key = `${seat.sessionId}/${name}`;
        if (this.pulled.has(key)) continue;
        this.pulled.add(key);
        void this.pull(seat, name, key);
      }
    }

    for (const id of [...this.peerMap.keys()]) {
      if (seen.has(id)) continue;
      this.peerMap.delete(id);
      this.deps.remoteAudio.detach(id);
      this.detector.unwatch(id);
    }

    this.emit('rosterChanged', this.peers());
  }

  private pull(seat: RosterSeat, name: string, key: string): Promise<void> {
    return this.queue
      .run(`pull ${key}`, async () => {
        const pc = this.pc;
        if (!pc || !this.sessionId) return;

        const answer = await this.deps.signalling.pull(this.sessionId, [
          { location: 'remote', sessionId: seat.sessionId, trackName: name }
        ]);

        const kind = trackKind(name) ?? 'mic';
        for (const track of answer.tracks ?? []) {
          if (track.mid) this.expecting.set(track.mid, { participantId: seat.userId, kind });
        }

        // The SFU's description of what it is about to send. This is the
        // direction where it offers and we answer — and the exchange is not
        // over until the renegotiation has been accepted, which is why the
        // `await` below is inside the queued task rather than after it.
        if (answer.requiresImmediateRenegotiation && answer.sessionDescription) {
          await pc.setRemoteDescription(answer.sessionDescription);
          const ours = await pc.createAnswer();
          await pc.setLocalDescription(ours);
          await this.deps.signalling.renegotiate(this.sessionId, {
            sdp: ours.sdp,
            type: 'answer'
          });
        }
      })
      .catch((error: unknown) => {
        // One peer failing is not the call failing. Forgotten rather than
        // retried, so the next roster offers them again — which is what
        // happens when somebody's connection recovers.
        this.pulled.delete(key);
        if (!(error instanceof NegotiationCancelled)) this.report(error);
      });
  }

  private onRemoteTrack(event: TrackEventLike): void {
    const mid = event.transceiver.mid;
    const want = mid ? this.expecting.get(mid) : undefined;
    if (!want) return;

    const peer = this.peerMap.get(want.participantId);
    if (!peer) return;

    // Audio is heard, video is drawn. A screen share's audio track arrives
    // under a `screen-` name and belongs to the first of those, not the second:
    // `RemoteTracks.screen` is what a tile renders.
    if (event.track.kind === 'audio') {
      this.deps.remoteAudio.attach(want.participantId, event.track);
      this.detector.watch(want.participantId, event.track);
      if (want.kind !== 'mic') return;
    }

    peer.tracks = { ...peer.tracks, [want.kind]: event.track };
    this.emit('trackAdded', want.participantId, want.kind, event.track);
    this.emit('rosterChanged', this.peers());
  }

  private onSpeaking(id: string, speaking: boolean): void {
    const peer = this.peerMap.get(id);
    if (peer) peer.speaking = speaking;
    this.emit('speaking', id, speaking);
  }

  // ── the heartbeat, and the seat that went missing ─────────────────────────

  private startPolling(): void {
    if (this.pollHandle !== null) return;
    this.pollHandle = this.deps.setTimer(() => void this.poll(), ROSTER_POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollHandle === null) return;
    this.deps.clearTimer(this.pollHandle);
    this.pollHandle = null;
  }

  private async poll(): Promise<void> {
    if (!this.sessionId || this.rejoining) return;
    let answer;
    try {
      answer = await this.deps.signalling.roster(this.deps.deviceId);
    } catch {
      return; // the next tick tries again
    }
    if (!this.sessionId) return;

    if (answer.since) this.since = answer.since;
    if (answer.callId) this.callId = answer.callId;
    if (answer.recording !== undefined) this.applyRecording(answer.recording);

    // Our own seat is gone: swept while this tab was asleep, or the server
    // restarted. Re-joining under the same call id keeps it the same call to
    // everybody else — a fresh id would start a second one beside it.
    if (!answer.roster.some((seat) => seat.sessionId === this.sessionId)) {
      void this.rejoin();
      return;
    }

    this.applyRoster(answer.roster);
  }

  /**
   * A `callstate` frame, handed in by whoever owns the socket.
   *
   * It cannot be pulled from — the frame carries no SFU session id, on purpose
   * — so it does two things: it notices this device's seat has gone, and it
   * brings the next roster read forward instead of waiting out the poll.
   */
  applyCallState(seats: Array<{ deviceId: string }>): void {
    if (!this.sessionId || this.rejoining) return;
    if (!seats.some((seat) => seat.deviceId === this.deps.deviceId)) {
      void this.rejoin();
      return;
    }
    void this.poll();
  }

  private async rejoin(): Promise<void> {
    if (this.rejoining || !this.sessionId) return;
    this.rejoining = true;
    try {
      this.queue.clear();
      this.dropConnection();

      const ice = this.deps.iceServers();
      const seat = await this.deps.signalling.join(this.deps.deviceId, this.callId);
      this.adoptSession(seat.sessionId, seat.callId, seat.since);
      this.pc = this.openConnection(await ice);
      await this.publishLocal();
      this.applyRoster(seat.roster);
    } catch (error) {
      this.fail(error);
    } finally {
      this.rejoining = false;
    }
  }

  /** Everything that belonged to the old SFU session. The local tracks stay
   *  open: the microphone is still the same microphone, and asking for it again
   *  is a second permission prompt in the middle of a call. */
  private dropConnection(): void {
    this.pc?.close();
    this.pc = null;
    this.published.length = 0;
    this.pulled.clear();
    this.expecting.clear();
    for (const id of this.peerMap.keys()) {
      this.deps.remoteAudio.detach(id);
      this.detector.unwatch(id);
    }
    this.peerMap.clear();
  }

  // ── leaving ───────────────────────────────────────────────────────────────

  async leave(): Promise<void> {
    // Re-entrant on purpose: the overlay's Hang up leaves and then tells
    // whoever owns the call, which leaves again before the first `DELETE` has
    // answered. Two of those is two `left` events and a second row for one
    // call.
    if (this.leaving || this.status === 'idle' || this.status === 'ended') return;
    this.leaving = true;
    const sessionId = this.sessionId;
    // Read before the teardown clears them. The server answers with its own
    // `since` and `callId` while the roster still holds somebody and with
    // nothing once it is empty, so ours are the fallback rather than the
    // second opinion.
    const since = this.since;
    const callId = this.callId;
    this.teardown();
    if (sessionId) {
      const answer = await this.deps.signalling.leave(this.deps.deviceId).catch(() => null);
      this.emit('left', {
        last: answer?.last === true,
        since: answer?.since || since,
        callId: answer?.callId || callId
      });
    }
    this.leaving = false;
    this.setStatus('ended');
  }

  /** Both endings share this. The seat is given back even when the call failed:
   *  a roster row for a client that is not there makes every later joiner pull
   *  a track nobody is sending. */
  private teardown(): void {
    this.stopPolling();
    this.queue.clear();
    void this.chunks?.stop();
    this.chunks = null;
    this.recording.active = false;
    this.detector.stop();
    this.deps.remoteAudio.stop();
    this.deps.background.stop();
    this.dropConnection();
    for (const track of [this.micTrack, this.camTrack, this.camSource, ...this.screenTracks]) {
      track?.stop();
    }
    this.micTrack = null;
    this.camTrack = null;
    this.camSource = null;
    this.screenTracks = [];
    this.sessionId = '';
    this.local = { ...this.local, mic: false, cam: false, screen: false };
  }

  private fail(error: unknown): void {
    const sessionId = this.sessionId;
    this.teardown();
    if (sessionId) void this.deps.signalling.leave(this.deps.deviceId).catch(() => {});
    this.setStatus('ended');
    this.report(error);
  }

  // ── controls ──────────────────────────────────────────────────────────────

  /** Mute sends silence. `track.stop()` would end the track and need a
   *  renegotiation to undo, and a microphone light that goes out and comes back
   *  on every mute is how somebody learns not to trust the button. */
  setMuted(muted: boolean): void {
    this.local = { ...this.local, muted };
    if (this.micTrack) this.micTrack.enabled = !muted;
  }

  /**
   * A different microphone, without a renegotiation.
   *
   * `replaceTrack` swaps what a sender is sending: no offer, no answer, and the
   * far end hears the change with no gap. Opening the new track before stopping
   * the old one means a device that refuses leaves the call still talking.
   */
  async setMicrophone(deviceId: string): Promise<void> {
    const previous = this.micTrack;
    await this.openMicrophone(deviceId);
    const publication = this.published.find((p) => p.kind === 'mic');
    if (publication && this.micTrack) {
      await publication.transceiver.sender.replaceTrack(this.micTrack);
      publication.track = this.micTrack;
    }
    previous?.stop();
    if (this.recording.active && this.chunks) {
      // The recorder holds the track it was handed, so it is restarted on the
      // new one. The chunk in flight finishes and uploads as itself.
      await this.chunks.stop();
      this.recording.active = false;
      this.applyRecording(true);
    }
  }

  /** Echo cancellation, noise suppression and gain control, applied to the live
   *  track and kept for the next one. */
  async setAudioProcessing(settings: AudioSettings): Promise<void> {
    this.audio = settings;
    await this.micTrack?.applyConstraints(settings).catch(() => {});
  }

  setDeafened(deafened: boolean): void {
    this.local = { ...this.local, deafened };
    this.deps.remoteAudio.setMuted(deafened);
  }

  /** Held, not toggled: `true` on key-down, `false` on key-up. */
  setPushToTalk(active: boolean): void {
    this.local = { ...this.local, pushToTalk: active, muted: !active };
    if (this.micTrack) this.micTrack.enabled = active;
  }

  async setCamera(on: boolean, deviceId?: string): Promise<void> {
    if (on === this.local.cam && !deviceId) return;
    if (!on) {
      await this.unpublish('cam');
      this.deps.background.stop();
      this.camSource?.stop();
      this.camSource = null;
      this.camTrack = null;
      this.local = { ...this.local, cam: false };
      return;
    }
    await this.openCamera(deviceId);
    await this.publishLocal();
  }

  async setScreen(on: boolean): Promise<void> {
    if (on === this.local.screen) return;
    if (!on) {
      await this.unpublish('screen');
      for (const track of this.screenTracks) track.stop();
      this.screenTracks = [];
      this.local = { ...this.local, screen: false };
      return;
    }

    const stream = await this.deps.media.getDisplayMedia({ video: true, audio: true });
    const video = stream.getVideoTracks()[0];
    if (!video) throw new Error('Nothing was shared.');
    // The system's own "Stop sharing" ends the track without telling this app,
    // so the track is what is listened to rather than the browser's button.
    video.addEventListener('ended', () => void this.setScreen(false));
    this.screenTracks = [video, ...stream.getAudioTracks()];
    this.local = { ...this.local, screen: true };
    await this.publishLocal();
  }

  /** The published camera track is swapped, not renegotiated: the far end sees
   *  the background change with no interruption. */
  async setBackground(background: Background): Promise<void> {
    this.background = background;
    if (!this.camSource) return;
    const next = await this.deps.background.apply(this.camSource, background);
    this.camTrack = next;
    const publication = this.published.find((p) => p.kind === 'cam');
    if (publication) {
      await publication.transceiver.sender.replaceTrack(next);
      publication.track = next;
    }
  }

  devices(): Promise<DeviceList> {
    return listDevices(this.deps.media);
  }

  setSpeaker(deviceId: string): Promise<void> {
    return this.deps.remoteAudio.setSpeaker(deviceId);
  }

  async stats(): Promise<CallStats> {
    const pc = this.pc;
    if (!pc) return { rttMs: 0, packetLossPercent: 0, outboundKbps: 0, inboundKbps: 0 };

    let rttMs = 0;
    let lost = 0;
    let received = 0;
    let inBytes = 0;
    let outBytes = 0;

    const report = await pc.getStats();
    report.forEach((stat) => {
      const num = (key: string): number => (typeof stat[key] === 'number' ? (stat[key] as number) : 0);
      if (stat.type === 'candidate-pair' && stat.state === 'succeeded') {
        rttMs = Math.round(num('currentRoundTripTime') * 1000);
      }
      if (stat.type === 'inbound-rtp') {
        lost += num('packetsLost');
        received += num('packetsReceived');
        inBytes += num('bytesReceived');
      }
      if (stat.type === 'outbound-rtp') outBytes += num('bytesSent');
    });

    const at = this.deps.now();
    const last = this.lastStats;
    const seconds = last ? Math.max(0.001, (at - last.at) / 1000) : 0;
    const rate = (bytes: number, before: number): number =>
      seconds === 0 ? 0 : Math.max(0, Math.round(((bytes - before) * 8) / 1000 / seconds));
    const stats: CallStats = {
      rttMs,
      packetLossPercent: received + lost === 0 ? 0 : Math.round((lost / (received + lost)) * 1000) / 10,
      inboundKbps: last ? rate(inBytes, last.inBytes) : 0,
      outboundKbps: last ? rate(outBytes, last.outBytes) : 0
    };
    this.lastStats = { at, inBytes, outBytes };
    return stats;
  }

  // ── recording and bookmarks ───────────────────────────────────────────────

  private meetingPath(): string {
    if (this.room.kind !== 'meeting') throw new Error('That is a call, not a meeting.');
    return roomPath(this.room);
  }

  private async setRecordingFlag(active: boolean): Promise<void> {
    const path = `${this.meetingPath()}/recording/${active ? 'start' : 'stop'}`;
    await this.deps.request<unknown>(path, { method: 'POST' });
    this.applyRecording(active);
  }

  /**
   * The meeting-wide flag, from whichever end set it.
   *
   * A host starting a recording starts everybody's uploader, and a host
   * stopping it stops everybody's — each participant records its own
   * microphone, so the flag is the only thing that is shared.
   */
  private applyRecording(active: boolean): void {
    if (active === this.recording.active) return;
    this.recording.active = active;

    if (!active) {
      void this.chunks?.stop();
      this.chunks = null;
    } else if (this.micTrack && this.room.kind === 'meeting') {
      this.chunks = new ChunkRecorder({
        track: this.micTrack,
        sinceMs: this.since,
        upload: this.deps.uploadChunk(this.room.id),
        createRecorder: this.deps.createRecorder,
        onError: (error) => this.report(error),
        now: this.deps.now
      });
      this.chunks.start();
    }

    this.emit('recordingChanged', active);
  }

  async mark(label?: string): Promise<void> {
    const atMs = Math.max(0, Math.round(this.deps.now() - this.since));
    await this.deps.request<unknown>(`${this.meetingPath()}/marks`, {
      method: 'POST',
      body: JSON.stringify({ atMs, ...(label ? { label } : {}) })
    });
  }

  // ── events ────────────────────────────────────────────────────────────────

  on<E extends CallEvent>(event: E, cb: CallEvents[E]): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(cb);
    return () => set.delete(cb);
  }

  /** For whoever owns the lobby: the media never learns a decision by itself. */
  setAdmission(admission: Admission): void {
    this.emit('admissionChanged', admission);
  }

  private setStatus(status: CallStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit('statusChanged', status);
  }

  private report(error: unknown): void {
    this.emit('error', error instanceof Error ? error : new Error(String(error)));
  }

  private emit<E extends CallEvent>(event: E, ...args: Parameters<CallEvents[E]>): void {
    for (const cb of this.handlers.get(event) ?? []) {
      (cb as (...a: Parameters<CallEvents[E]>) => void)(...args);
    }
  }
}

/** The one construction site. Nothing reaches the network or the devices until
 *  `join()`, so a room can render before anybody has allowed anything. */
export const openCallSession = (room: Room, deps?: Partial<CallDeps>): LiveCallSession =>
  new LiveCallSession(room, { ...browserDeps(room), ...deps });
